import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  completeOnboarding,
  deleteUserSettings,
  refreshProfilePicture,
  updateUserSettings,
  updateCurrentUserSettings,
  type DeleteUserSettingsResponse,
  type UserSettings,
  type UserSettingsUpdate,
} from '@/api/userSettings';
import { queryKeys } from '@/lib/queryKeys';

function syncUserSettingsCache(queryClient: ReturnType<typeof useQueryClient>, data: UserSettings) {
  queryClient.setQueryData(queryKeys.userSettings.current(), data);

  if (data.id) {
    queryClient.setQueryData(queryKeys.userSettings.detail(data.id), data);
  }

  queryClient.setQueryData<UserSettings[] | undefined>(
    queryKeys.userSettings.list(),
    (currentUsers) => {
      if (!currentUsers) {
        return currentUsers;
      }

      const existingIndex = currentUsers.findIndex((user) => user.id === data.id);
      if (existingIndex === -1) {
        return currentUsers;
      }

      const nextUsers = [...currentUsers];
      nextUsers[existingIndex] = data;
      return nextUsers;
    }
  );
}

export function useUpdateCurrentUserSettingsMutation(
  options?: UseMutationOptions<UserSettings, Error, UserSettingsUpdate>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (settings) => updateCurrentUserSettings(settings),
    onSuccess: async (data, variables, onMutateResult, context) => {
      syncUserSettingsCache(queryClient, data);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export interface UpdateUserSettingsInput {
  userId: string;
  settings: UserSettingsUpdate;
}

export function useUpdateUserSettingsMutation(
  options?: UseMutationOptions<UserSettings, Error, UpdateUserSettingsInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({ userId, settings }) => updateUserSettings(userId, settings),
    onSuccess: async (data, variables, onMutateResult, context) => {
      syncUserSettingsCache(queryClient, data);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useRefreshProfilePictureMutation(
  options?: UseMutationOptions<UserSettings, Error, void>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: refreshProfilePicture,
    onSuccess: async (data, variables, onMutateResult, context) => {
      syncUserSettingsCache(queryClient, data);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useCompleteOnboardingMutation(
  options?: UseMutationOptions<UserSettings, Error, void>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: completeOnboarding,
    onSuccess: async (data, variables, onMutateResult, context) => {
      syncUserSettingsCache(queryClient, data);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useDeleteUserSettingsMutation(
  options?: UseMutationOptions<DeleteUserSettingsResponse, Error, string>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (userId) => deleteUserSettings(userId),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.list() });
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
